import React, { useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { useAppDispatch } from "../redux/hook";
import { sendEmail } from "../redux/actions/sendEmailAction";

const CalendarComponent = (props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [open, setOpen] = React.useState(false);
  const [events, setEvents] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");
  const [selectedTenant, setSelectedTenant] = useState("");
  const [selectedPrice, setSelectedPrice] = useState("");
  const [selectedDescription, setSelectedDescription] = useState("");
  const [isEvent, setIsEvent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
  });

  const handleClose = () => {
    setOpen(false);
    setForm((prev) => ({
      ...prev,
      name: "",
    }));
    setForm((prev) => ({
      ...prev,
      email: "",
    }));
  };

  // Example fetching from your API or database
  useEffect(() => {
    // Simulate async API call
    const fetchData = async () => {
      const dataFromDb = props.events.map((ev) => ({
        title: `${ev.name} - ${ev.tenant}`,
        start: `${ev.start_date}T${ev.start_time}`,
        end: `${ev.end_date}T${ev.end_time}`,
        extendedProps: {
          tenant: ev.tenant,
          price: ev.price,
          description: ev.description,
        },
      }));

      setEvents(dataFromDb);
    };
    fetchData();
  }, [props.events]);

  const handleDateClick = (info) => {
    //alert(`You clicked on date: ${info.dateStr}`);
    setSelectedDate(info.dateStr);
    setOpen(true);
    setIsEvent(false);
  };

  const handleEventClick = (info) => {
    const { title, extendedProps } = info.event;
    setSelectedTitle(title);
    setSelectedTenant(extendedProps.tenant);
    setSelectedPrice(extendedProps.price);
    setSelectedDescription(extendedProps.description);
    setOpen(true);
    setIsEvent(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isFormValid = form.name?.trim() !== "" && form.email?.trim() !== "";

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSendingEmail(true);

    const emailData = {
      subject: t("external.space.subject"),
      name: form.name,
      email: form.email,
      phone: form.phone,
      addres: form.address,
      message: moment(selectedDate).format("DD/MM/YYYY"),
    };

    const result = await dispatch(sendEmail(emailData));

    if (result.payload === "email_sent_successfully") {
      alert(`${t("common.email_sent_successfully")}`);
    } else {
      alert(`${t("common.email_sent_unsuccessfully")}`);
    }
    setSendingEmail(false);

    handleClose();
  };

  const maskPhone = (value) => {
    if (!value) return value;
    const digits = value.replace(/\D/g, "").slice(0, 11); // max 11 digits (2 + 5 + 4)
    const d1 = digits.slice(0, 2);
    const d2 = digits.slice(2, 7);
    const d3 = digits.slice(7, 11);

    if (digits.length === 0) return "";
    if (digits.length <= 2) return `(${d1}`;
    if (digits.length <= 7) return `(${d1}) ${d2}`;
    return `(${d1}) ${d2}-${d3}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = maskPhone(e.target.value);
    setForm((prev) => ({
      ...prev,
      phone: formatted,
    }));
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">
        {t("external.space.events_of_association")}
      </h2>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        events={events}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        height="auto"
      />

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            height: "35vh",
            width: "80vh",
            maxHeight: "none",
          },
        }}
      >
        <DialogTitle>{t("external.space.event")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isEvent ? (
              <>
                <b>{t("external.space.event")}:</b> {selectedTitle}
                <br />
                <b>{t("external.space.price")}:</b> R${selectedPrice}
                <br />
                <b>{t("external.space.responsible")}:</b> {selectedTenant}
                <br />
                <b>{t("external.space.description")}:</b> {selectedDescription}
                <br />
              </>
            ) : (
              <>
                {t(
                  "external.space.please_fill_in_your_name_and_email_to_reserve_the_date"
                )}{" "}
                {moment(selectedDate).format("DD/MM/YYYY")}
              </>
            )}
          </DialogContentText>
          {!isEvent && (
            <form onSubmit={handleSubmit} id="subscription-form">
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div style={{ width: "50%" }}>
                  <TextField
                    id="name"
                    name="name"
                    type="text"
                    className="form-input w-full py-2 px-3 h-10 bg-transparent border border-inherit dark:border-gray-800 dark:bg-slate-900 dark:text-slate-200 rounded-xl outline-none focus:border-orange-500/50 dark:focus:border-orange-500/50 focus:ring-0 mt-2"
                    placeholder={`${t("external.space.name")}${" : "}`}
                    onChange={handleChange}
                    value={form.name || ""}
                  />
                </div>
                <div style={{ width: "50%" }}>
                  <TextField
                    id="name"
                    name="email"
                    type="email"
                    //fullWidth
                    className="form-input w-full py-2 px-3 h-10 bg-transparent border border-inherit dark:border-gray-800 dark:bg-slate-900 dark:text-slate-200 rounded-xl outline-none focus:border-orange-500/50 dark:focus:border-orange-500/50 focus:ring-0 mt-2"
                    placeholder={`${t("external.space.email")}${" : "}`}
                    onChange={handleChange}
                    value={form.email || ""}
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "20px",
                }}
              >
                <div style={{ width: "50%" }}>
                  <TextField
                    id="phone"
                    name="phone"
                    type="text"
                    className="form-input w-full py-2 px-3 h-10 bg-transparent border border-inherit dark:border-gray-800 dark:bg-slate-900 dark:text-slate-200 rounded-xl outline-none focus:border-orange-500/50 dark:focus:border-orange-500/50 focus:ring-0 mt-2"
                    placeholder={`${t("external.space.phone")}${" : "}`}
                    onChange={handlePhoneChange}
                    value={form.phone || ""}
                  />
                </div>
                <div style={{ width: "50%" }}>
                  <TextField
                    id="address"
                    name="address"
                    type="text"
                    className="form-input w-full py-2 px-3 h-10 bg-transparent border border-inherit dark:border-gray-800 dark:bg-slate-900 dark:text-slate-200 rounded-xl outline-none focus:border-orange-500/50 dark:focus:border-orange-500/50 focus:ring-0 mt-2"
                    placeholder={`${t("external.space.address")}${" : "}`}
                    onChange={handleChange}
                    value={form.address || ""}
                  />
                </div>
              </div>
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            id="submit"
            name="send"
            onClick={handleClose}
            className={`py-2 px-5 inline-block font-normal tracking-wide border align-middle transition duration-500 ease-in-out text-base text-center bg-gray-500 hover:bg-gray-600 border-gray-500 hover:border-gray-600 text-black rounded-xl ${
              !isFormValid ? "opacity-90 cursor-not-allowed" : ""
            }`}
          >
            {t("external.space.cancel")}
          </Button>
          {!isEvent ? (
            !isFormValid || sendingEmail ? (
              <Button
                type="submit"
                form="subscription-form"
                id="submit"
                name="send"
                disabled={!isFormValid || sendingEmail}
                className={`py-2 px-5 inline-block font-normal tracking-wide border align-middle transition duration-500 ease-in-out text-base text-center bg-gray-500 hover:bg-gray-600 border-gray-500 hover:border-gray-600 text-black rounded-xl ${
                  !isFormValid ? "opacity-90 cursor-not-allowed" : ""
                }`}
              >
                {t("external.space.send")}
              </Button>
            ) : (
              <Button
                type="submit"
                form="subscription-form"
                id="submit"
                name="send"
                disabled={!isFormValid || sendingEmail}
                className={`py-2 px-5 inline-block font-normal tracking-wide border align-middle transition duration-500 ease-in-out text-base text-center bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600 text-white rounded-xl ${
                  !isFormValid ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {t("external.space.send")}
              </Button>
            )
          ) : null}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CalendarComponent;
